import React, { useContext, useMemo } from 'react';
import Mediator, { useMediator } from '@core-ui/mediator';
import { useLocalize } from '@core-ui/localization-provider';
import { customAttribute } from '@core-ui/reporting';
import { ProductOptions } from '../product-options';
import {
  LEFT_ARROW_ARIA_LABEL,
  OF_TEXT,
  PAGE_TEXT,
  PRODUCT_GRID_REGION_ERROR_DESCRIPTION,
  PRODUCT_GRID_REGION_NO_PRODUCT_ERROR_DESCRIPTION,
  RIGHT_ARROW_ARIA_LABEL,
} from './localization-tokens';
import { BreakpointContext, XLARGE } from '@core-ui/breakpoint-provider';
import { StitchInverseStyleProvider } from '@core-ui/react-stitch';
import universal from '@core-ui/universal';
import { adaptOrderedOptions } from './adapt-applied-options';
import TopBar from './styled/TopBar';
import useBRRedesign from '../../hooks/features/useBRRedesign';
import ProductGridDivision from './styled/ProductGridDivision';
import ErrorMessageDivision from './styled/ErrorMessageDivision';
import PaginatorWrapper from './styled/PaginatorWrapper';
import GridPlaceholder from './components/GridPlaceholder';
import QuickFilterWrapper from '../quick-filter-wrapper';
import ProductGrid from './components/ProductGrid';
import { buildColorSwatchesDataLayer } from '../../datalayer/events/color-swatches-engagement';

const Paginator = universal(import('@core-ui/paginator'));
const InlineFacetTags = universal(import('@cat/inline-facet-tags'), {
  key: 'default',
});

const getProductGridRegionErrorMsg = (error, localize) => {
  return error
    ? localize(PRODUCT_GRID_REGION_ERROR_DESCRIPTION)
    : localize(PRODUCT_GRID_REGION_NO_PRODUCT_ERROR_DESCRIPTION);
};

const getDesktopFragment = ({
  isDesktop,
  appliedOptions,
  onClear,
  isModelToggleEnabled,
  isMixedGridEnabled,
  publish,
  brand,
  facets,
  isBRRedesignEnabled,
}) => {
  const facetOptions = facets?.facetOptions;
  const appliedFacets = facets?.appliedFacets;
  const quickFilterWrapperParams = {
    facetOptions,
    abbrBrand: brand,
    publish,
    appliedOptions,
    appliedFacets,
  };
  return (
    isDesktop &&
    !isBRRedesignEnabled && (
      <>
        <QuickFilterWrapper
          key="quick-filter-wrapper"
          {...quickFilterWrapperParams}
        />
        <InlineFacetTags facetOptions={appliedOptions} onClear={onClear} />
        <ProductOptions
          isModelToggleEnabled={isModelToggleEnabled}
          isMixedGridEnabled={isMixedGridEnabled}
          publish={publish}
          brand={brand}
        />
      </>
    )
  );
};

const getHasProducts = (productGroups) => {
  return productGroups?.[0] ?? false;
};

const deferredResolveCat1ImageLoaded = (cat1LoadedEvent) => {
  if (!window.gap.isCat1ImageLoadedSent && cat1LoadedEvent) {
    window.gap.isCat1ImageLoadedSent = true;
    cat1LoadedEvent.deferred.resolve('cat1ImageLoaded');
  }
};

const ProductGridRegion = (props) => {
  const {
    brand,
    cid,
    highPriorityEvents,
    isModelToggleEnabled,
    isMixedGridEnabled,
  } = props;

  const { state, publish } = useMediator(
    Mediator.mediators['left-faceted-grid']
  );
  const { localize } = useLocalize();
  const { minWidth } = useContext(BreakpointContext);
  const {
    disablePlaceholders,
    error,
    ignoredFacets,
    productGroups,
    paginator,
    totalItemCount,
    orderedFacets,
    facets,
  } = state;
  const isBRRedesignEnabled = useBRRedesign();
  const isDesktop = minWidth(XLARGE);

  const { onFirstCarouselArrowClick, onFirstColorSwatchClick } =
    buildColorSwatchesDataLayer(props);

  const appliedOptions = useMemo(
    () => adaptOrderedOptions(orderedFacets, ignoredFacets),
    [orderedFacets, ignoredFacets]
  );
  const onClear = (option) =>
    publish('updateFacets', [option, undefined, false]);

  // eslint-disable-next-line complexity
  const getProductGrid = () => {
    const hasProducts = getHasProducts(productGroups);
    const hasPlaceholders = !disablePlaceholders;

    const desktopFragment = getDesktopFragment({
      isDesktop,
      appliedOptions,
      onClear,
      isModelToggleEnabled,
      isMixedGridEnabled,
      publish,
      brand,
      facets,
      isBRRedesignEnabled,
    });

    if (hasPlaceholders) {
      return (
        <>
          <TopBar isDesktop={isDesktop}>{desktopFragment}</TopBar>
          <GridPlaceholder />
        </>
      );
    }

    if (hasProducts) {
      return (
        <>
          <TopBar isDesktop={isDesktop}>{desktopFragment}</TopBar>
          <ProductGrid
            productGroups={productGroups}
            paginator={paginator}
            totalItemCount={totalItemCount}
            onFirstColorSwatchClick={onFirstColorSwatchClick}
            onFirstCarouselArrowClick={onFirstCarouselArrowClick}
            {...props}
          />
          <PaginatorWrapper isBRRedesignEnabled={isBRRedesignEnabled}>
            <StitchInverseStyleProvider invert={isBRRedesignEnabled}>
              <Paginator
                goToPage={(pageIndex) =>
                  publish('goToPage', [{ goToPage: pageIndex }])
                }
                index={Number(paginator.pageNumberRequested)}
                numberOfPages={Number(paginator.pageNumberTotal)}
                ofText={localize(OF_TEXT)}
                pageText={localize(PAGE_TEXT)}
                rightArrowLabel={localize(RIGHT_ARROW_ARIA_LABEL)}
                leftArrowLabel={localize(LEFT_ARROW_ARIA_LABEL)}
              />
            </StitchInverseStyleProvider>
          </PaginatorWrapper>
        </>
      );
    }

    const cat1LoadedEvent = highPriorityEvents.find(
      ({ name }) => name === 'cat1ImageLoaded'
    );

    customAttribute('cat1NoProductsInGrid', cid);
    deferredResolveCat1ImageLoaded(cat1LoadedEvent);
    const productGridRegionErrorMsg = getProductGridRegionErrorMsg(
      error,
      localize
    );

    return (
      <>
        <TopBar isDesktop={isDesktop}>{desktopFragment}</TopBar>
        <ErrorMessageDivision
          className="faceted-grid__message"
          isBRRedesignEnabled={isBRRedesignEnabled}
        >
          {productGridRegionErrorMsg}
        </ErrorMessageDivision>
      </>
    );
  };

  return (
    <ProductGridDivision
      className="faceted-grid"
      isBRRedesignEnabled={isBRRedesignEnabled}
    >
      <div id="faceted-grid">{getProductGrid()}</div>
    </ProductGridDivision>
  );
};

export default ProductGridRegion;
